<template>
  <div class="map" id="map">
    <tooltip-component v-if="tooltip" :layer="tileSetLayer" />
  </div>
</template>

<script>

import { createStyle } from 'util/layer/StyleBuilder'
import TooltipComponent from './Tooltip'
import { colorFromArray, deepCopy } from 'util/helpers'

/**
 * App component
 */
export default {
  name: 'MapComponent',
  components: {
    TooltipComponent
  },
  data () {
    return {
      layers: {},
      activeBaseLayer: null,
      tileSetLayer: null,
      viewPadding: {
        left: 0,
        bottom: 0
      },
      parcel: null,
      mapSelect: false,
      tooltip: false,
      timebarProperty: null,
      timebarValue: null,
      mapFilter: null
    }
  },
  mounted () {
    this.layers.base = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.layers.overlays = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.createBaseLayers(this.$conf.map.layers.baseLayers, this.$conf.map.baseLayer)
    this.createOverlays(this.$conf.map.layers.overlays)
    this.createMap()
    this.subscribe()
  },
  methods: {
    createMap () {
      this.$map.set({
        layers: Object.keys(this.layers).map(i => this.layers[i])
      })
    },
    createBaseLayers (layers, activeBaseLayerId) {
      Object.keys(layers).forEach(id => {
        layers[id].visible = (id === activeBaseLayerId)
        layers[id].id = id
        layers[id].zIndex = 0
        const layer = this.$map.createLayer(layers[id])
        this.$map.addLayer(layer, this.layers.base)
      })
    },
    createOverlays (layers) {
      layers.forEach(layer => {
        this.$map.addLayer(this.$map.createLayer(layer), this.layers.overlays)
      })
    },
    changeBaseLayer (layerid) {
      this.layers.base.getLayers().forEach(layer => {
        if (layer.get('id') === layerid) {
          layer.setVisible(true)
        } else {
          layer.setVisible(false)
        }
      })
    },
    subscribe () {
      // base layer change
      this.$store.watch(
        () => this.$store.getters['baseLayer'],
        layer => {
          if (layer) {
            this.changeBaseLayer(layer)
          }
        }
      )
      // tileset change
      this.$store.watch(
        () => this.$store.getters['tileSetConf'],
        tileSetConf => {
          if (this.tileSetLayer) {
            this.removeTooltip(this.tileSetLayer)
            this.$map.removeLayer(this.tileSetLayer, this.layers.overlays)
          }
          this.tileSetLayer = this.createTileSetLayer(tileSetConf)
          if (this.$conf.tilesets.zoomTo) {
            this.$map.fitTo({
              bbox: tileSetConf.bounds,
              srid: 'EPSG:4326'
            })
          }
          this.$map.addLayer(this.tileSetLayer, this.layers.overlays)
          if (this.$conf.tilesets.select && this.$conf.tilesets.select.tooltip) {
            this.addTooltip(this.tileSetLayer)
          }
        }
      )
      // property change
      this.$store.watch(
        () => this.$store.getters['tileSetProperty'],
        tileSetProperty => {
          this.timebarProperty = tileSetProperty
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // parcel change
      this.$store.watch(
        () => this.$store.getters['parcel'],
        parcel => {
          this.parcel = parcel
          this.selected = parcel ? parcel[this.$conf.search.id] : null
          if (parcel) {
            if (this.tileSetLayer && !this.mapSelect) {
              this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
              this.mapSelect = false
            }
          } else {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // timebar value change
      this.$store.watch(
        () => this.$store.getters['timebarValue'],
        value => {
          this.timebarValue = value
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // filter change
      this.$store.watch(
        () => this.$store.getters['filter'],
        value => {
          this.mapFilter = value
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // click
      if (this.$conf.tilesets.select && this.$conf.tilesets.select.enabled) {
        this.$map.click({
          minZoom: this.$conf.tilesets.select.minZoom,
          maxZoom: this.$conf.tilesets.select.maxZoom,
          cb: features => {
            if (features && features.length) {
              this.mapSelect = true
              // hack to get over search result component updated called twice
              this.$store.dispatch('clearSearch')
              this.selected = features[0].get('id')
              this.$store.dispatch('search', this.selected)
                .then((result) => {
                  this.mapSelect = false
                  // force redraw of layer style
                  this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
                })
                .catch(err => {
                  this.mapSelect = false
                })
            }
          }
        })
      }
    },
    createTileSetLayer (conf) {
      const colorStyles = {
        default: createStyle(this.$conf.tilesets.style.default)
      }
      Object.keys(this.$conf.tilesets.colors).forEach((k, i) => {
        const color = colorFromArray(this.$conf.tilesets.colors[k])
        const styleConf = Object.assign({}, deepCopy(this.$conf.tilesets.style.default))
        styleConf.stroke.color = color
        styleConf.fill.color = color
        colorStyles[i] = createStyle(styleConf)
      })
      const selectedStyle = createStyle(this.$conf.tilesets.style.selected)
      let labelStyle = null
      if (
        this.$conf.tilesets.style.labels &&
        this.$conf.tilesets.style.labels.enabled &&
        this.$conf.tilesets.style.labels.text
      ) {
        labelStyle = createStyle({
          geometry: feature => {
            const geometry = feature.getGeometry()
            if (geometry.getType() === 'MultiPolygon') {
              // Only render label for the widest polygon of a multipolygon
              const polygons = geometry.getPolygons()
              let widest = 0
              for (let i = 0, ii = polygons.length; i < ii; ++i) {
                const width = getWidth(polygons[i].getExtent())
                if (width > widest) {
                  widest = width
                  geometry = polygons[i]
                }
              }
            }
            return geometry
          },
          text: this.$conf.tilesets.style.labels.text
        })
      }
      const layerConf = {
        type: 'MVT',
        id: conf.id,
        title: 'Tile set',
        projection: 'EPSG:3857',
        url: this.$conf.tilesets.url + this.$conf.tilesets.key + '/' + conf.id + '/{z}/{x}/{y}.' + conf.format,
        extent: this.$map.transform({ bbox: conf.bounds, from: 'EPSG:4326' }),
        style: (feature, resolution, buildStyle) => {
          // select base style
          let baseStyle = 'default'
          let estimations = feature.get(this.timebarProperty)
          if (estimations) {
            estimations = estimations.split(';')
              .map(p => p.split(':'))
              .filter(pair => Number(pair[0]) <= this.timebarValue)
            if (estimations.length) {
              baseStyle = Number(estimations[estimations.length - 1][1])
            }
          }
          // filter
          if (baseStyle !== 'default' && this.mapFilter && baseStyle in this.mapFilter && this.mapFilter[baseStyle] === false) {
            return []
          }
          // fallback
          if (!(baseStyle in colorStyles)) {
            baseStyle = 'default'
          }
          const s = [colorStyles[baseStyle]]
          // selected
          if (this.selected && feature.get('id') === this.selected) {
            s.push(selectedStyle)
            // set selected
            this.$store.dispatch('selectedFeature', feature)
          }
          // label
          if (this.$map.get().getView().getZoom() > this.$conf.tilesets.style.labels.minZoom && labelStyle) {
            labelStyle.getText().setText(feature.get('id'))
            s.push(labelStyle)
          }
          return s
        },
        tileGrid: {
          minZoom: conf.minzoom,
          maxZoom: conf.maxzoom
        }
      }
      return this.$map.createLayer(layerConf)
    },
    addTooltip (layer) {
      this.tooltip = true
    },
    removeTooltip (layer) {
      this.tooltip = false
    }
  }
}



</script>

<style lang="stylus">
.map
  height: 100%
  .ol-zoom
    left: auto
    right: .5em
</style>
